import { getStatusLabel } from '../../../status-labels';
import type { BillStatus } from '../../../types/bills';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { ORDER_CHARGE_BILL_LOCKED_MESSAGE, orderChargeBillLock } from './orderChargeBillLock';
import { PURCHASE_ORDER_EXPENSES_EDITABLE_STATUSES, type PurchaseOrderStatus } from './purchaseOrder';

/**
 *   view ─────────────────────────────────────────▶ allow
 *   else ──▶ PO window ──▶ bill lock ──▶ row rule
 *            DRAFT..PENDING_LIQUIDATION   delete · duplicate: saved row
 *                                         clear: draft row
 */
export type PurchaseOrderExpenseFacts = {
  purchaseOrderStatus: PurchaseOrderStatus;
  /** The charge's own Bills and its split children's Bills. */
  billStatuses: readonly BillStatus[];
  isDraft: boolean;
};

export const PURCHASE_ORDER_EXPENSE_FIELDS = [
  'expenseType',
  'accountLevel',
  'ledgerAccount',
  'businessEntity',
  'chargePer',
  'quantity',
  'unitAmount',
  'description',
] as const;

export type PurchaseOrderExpenseField = (typeof PURCHASE_ORDER_EXPENSE_FIELDS)[number];

export const PURCHASE_ORDER_EXPENSE_OPS = ['clear', 'duplicate'] as const;
export type PurchaseOrderExpenseOp = (typeof PURCHASE_ORDER_EXPENSE_OPS)[number];

const actionNotApplicable = (): Decision =>
  deny('PURCHASE_ORDER_EXPENSE_ACTION_NOT_APPLICABLE', 'Not available for this charge.');

const decideEntity = (
  facts: PurchaseOrderExpenseFacts,
  action: Action<PurchaseOrderExpenseField, PurchaseOrderExpenseOp>
): Decision => {
  if (action.type === 'view') return allow();

  if (!PURCHASE_ORDER_EXPENSES_EDITABLE_STATUSES.includes(facts.purchaseOrderStatus)) {
    return deny(
      'PURCHASE_ORDER_CHARGES_LOCKED',
      `Charges are locked once the purchase order is ${getStatusLabel(facts.purchaseOrderStatus)}.`
    );
  }

  const billLock = orderChargeBillLock(
    facts.billStatuses,
    'PURCHASE_ORDER_EXPENSE_BILL_LOCKED',
    ORDER_CHARGE_BILL_LOCKED_MESSAGE
  );
  if (!billLock.allowed) return billLock;

  if (action.type === 'delete') return facts.isDraft ? actionNotApplicable() : allow();
  if (action.type === 'perform' && action.op === 'clear') return facts.isDraft ? allow() : actionNotApplicable();
  if (action.type === 'perform' && action.op === 'duplicate') return facts.isDraft ? actionNotApplicable() : allow();

  return allow();
};

export const purchaseOrderExpensePolicy: Policy<
  PurchaseOrderExpenseFacts,
  PurchaseOrderExpenseField,
  PurchaseOrderExpenseOp
> = definePolicy<PurchaseOrderExpenseFacts, PurchaseOrderExpenseField, PurchaseOrderExpenseOp>({
  kind: 'purchase order charge',
  fields: PURCHASE_ORDER_EXPENSE_FIELDS,
  ops: PURCHASE_ORDER_EXPENSE_OPS,
  requires: {},
  entity: decideEntity,
});
